import { getValidAccessToken } from './getValidAccessToken';
import { normalizeEnvKey } from './envKeyUtils';
import { decodeJwtParts } from './debug/jwtDebug';
import {
  normalizeCustomAiClue,
  normalizeCustomDelete,
  normalizeCustomGameCreate,
  normalizeCustomGamePublic,
  normalizeCustomGuess,
  normalizeCustomReveal,
  normalizeCustomRunStart,
  normalizeCustomUpload,
  normalizeCustomZoomUpdate,
  normalizeCreatorCustomGamesList,
} from './customGameResponse';

const CUSTOM_GAME_TIMEOUT_MS = 60_000;

const getFunctionsUrl = () => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  if (!supabaseUrl) throw new Error('Missing REACT_APP_SUPABASE_URL');
  return `${supabaseUrl.replace(/\/+$/, '')}/functions/v1/custom-game`;
};

const withTimeout = async (promise, timeoutMs) => {
  let timeoutId = null;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      const error = new Error(`Request timed out after ${timeoutMs}ms`);
      error.code = 'TIMEOUT';
      reject(error);
    }, timeoutMs);
  });
  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    clearTimeout(timeoutId);
  }
};

const resolveApiKey = () => {
  const jwtAnonRaw = normalizeEnvKey(process.env.REACT_APP_SUPABASE_JWT_ANON_KEY);
  const anonRaw = normalizeEnvKey(process.env.REACT_APP_SUPABASE_ANON_KEY);

  const jwtDecoded = decodeJwtParts(jwtAnonRaw);
  const jwtIsUsable =
    jwtDecoded?.partsCount === 3 && !jwtDecoded?.decodeError && jwtDecoded?.payload;

  const anonDecoded = decodeJwtParts(anonRaw);
  const anonLooksJwt =
    anonDecoded?.partsCount === 3 && !anonDecoded?.decodeError && anonDecoded?.payload;

  if (jwtIsUsable) return jwtAnonRaw;
  if (anonLooksJwt) return anonRaw;
  return null;
};

const parseJsonResponse = async (response) => {
  const text = await response.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  return { text, data };
};

const buildHeaders = async ({ requireAuth = false, includeContentType = true } = {}) => {
  const headers = {};
  if (includeContentType) headers['Content-Type'] = 'application/json';

  const apikey = resolveApiKey();
  if (apikey) headers.apikey = apikey;

  const accessToken = await getValidAccessToken();
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  } else if (requireAuth) {
    const error = new Error('Missing auth session.');
    error.code = 'UNAUTHORIZED';
    error.status = 401;
    throw error;
  }

  return headers;
};

const toApiError = ({ response, payload, text, fallback }) => {
  const error = new Error(payload?.error || text || fallback);
  error.code = payload?.code;
  error.status = response?.status || null;
  return error;
};

const requestAction = async ({
  action,
  body = {},
  formData = null,
  requireAuth = false,
  timeoutMs = CUSTOM_GAME_TIMEOUT_MS,
}) => {
  const headers = await buildHeaders({
    requireAuth,
    includeContentType: !formData,
  });

  const requestBody = formData || JSON.stringify({ action, ...body });
  if (formData) {
    formData.set('action', action);
  }

  const doRequest = async (requestHeaders) =>
    await withTimeout(
      fetch(getFunctionsUrl(), {
        method: 'POST',
        headers: requestHeaders,
        body: requestBody,
      }),
      timeoutMs,
    );

  let response = await doRequest(headers);
  if (response.status === 401 && headers.Authorization) {
    const refreshed = await getValidAccessToken({ forceRefresh: true });
    if (refreshed) {
      response = await doRequest({
        ...headers,
        Authorization: `Bearer ${refreshed}`,
      });
    }
  }

  const { text, data } = await parseJsonResponse(response);
  if (!response.ok) {
    throw toApiError({
      response,
      payload: data,
      text,
      fallback: 'Custom game request failed.',
    });
  }

  return data || {};
};

export const uploadCustomGameImage = async ({ file }) => {
  const formData = new FormData();
  formData.append('file', file);

  const data = await requestAction({
    action: 'upload_image',
    formData,
    requireAuth: true,
  });
  return normalizeCustomUpload(data);
};

export const createCustomGame = async (payload) => {
  const data = await requestAction({
    action: 'create',
    body: payload,
    requireAuth: true,
  });
  return normalizeCustomGameCreate(data);
};

export const getCustomGameBySlug = async ({ slug }) => {
  const data = await requestAction({
    action: 'get_by_slug',
    body: { slug },
    requireAuth: false,
  });
  return normalizeCustomGamePublic(data);
};

export const startCustomGameRun = async ({ slug }) => {
  const data = await requestAction({
    action: 'start_run',
    body: { slug },
    requireAuth: false,
  });
  return normalizeCustomRunStart(data);
};

export const submitCustomGameGuess = async ({ runId, guess }) => {
  const data = await requestAction({
    action: 'guess',
    body: { runId, guess },
    requireAuth: false,
  });
  return normalizeCustomGuess(data);
};

export const revealCustomGameRun = async ({ runId }) => {
  const data = await requestAction({
    action: 'reveal',
    body: { runId },
    requireAuth: false,
  });
  return normalizeCustomReveal(data);
};

export const generateCustomGameAiClue = async ({ gameId, slug, regenerate = false }) => {
  const action = regenerate ? 'ai_clue_regenerate' : 'ai_clue_generate';
  const data = await requestAction({
    action,
    body: {
      ...(gameId ? { gameId } : {}),
      ...(slug ? { slug } : {}),
    },
    requireAuth: true,
  });
  return normalizeCustomAiClue(data);
};

export const listCreatorCustomGames = async ({ limit = 50, offset = 0 } = {}) => {
  const data = await requestAction({
    action: 'list_creator_games',
    body: { limit, offset },
    requireAuth: true,
  });
  return normalizeCreatorCustomGamesList(data);
};

export const updateCustomGameZoom = async ({
  gameId,
  slug,
  zoomOriginX,
  zoomOriginY,
  zoomRadius,
  fullZoomOutRow,
  clueRevealRow,
  clueText,
}) => {
  const data = await requestAction({
    action: 'update_zoom',
    body: {
      ...(gameId ? { gameId } : {}),
      ...(slug ? { slug } : {}),
      zoomOriginX,
      zoomOriginY,
      zoomRadius,
      ...(fullZoomOutRow !== undefined ? { fullZoomOutRow } : {}),
      ...(clueRevealRow !== undefined ? { clueRevealRow } : {}),
      ...(clueText !== undefined ? { clueText } : {}),
    },
    requireAuth: true,
  });
  return normalizeCustomZoomUpdate(data);
};

export const deleteCreatorCustomGame = async ({ gameId, slug }) => {
  const data = await requestAction({
    action: 'delete_game',
    body: {
      ...(gameId ? { gameId } : {}),
      ...(slug ? { slug } : {}),
    },
    requireAuth: true,
  });
  return normalizeCustomDelete(data);
};
